import { create } from 'zustand';
import type { PaymentProviderStateDto } from '@ecom/contracts';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';

/**
 * Payment providers (ADR-025).
 *
 * The catalogue comes from the API, so a provider added server-side appears
 * here without releasing this app. Secrets are write-only: what comes back is a
 * mask, and nothing in this store ever holds a key that was typed — it is sent
 * and forgotten.
 */

interface PaymentsState {
  providers: PaymentProviderStateDto[];
  loading: boolean;
  saving: boolean;
  error: string | null;

  load: () => Promise<void>;
  update: (
    key: string,
    patch: { enabled?: boolean; testMode?: boolean; credentials?: Record<string, string> },
  ) => Promise<boolean>;
}

export const usePaymentsStore = create<PaymentsState>((set) => ({
  providers: [],
  loading: false,
  saving: false,
  error: null,

  async load() {
    set({ loading: true, error: null });

    try {
      const page = await withSession(() => adminApi.payments.providers());
      set({ providers: page.items, loading: false });
    } catch (error) {
      set({ loading: false, error: errorMessage(error, 'Could not load the payment providers.') });
    }
  },

  async update(key, patch) {
    set({ saving: true, error: null });

    try {
      const page = await withSession(() => adminApi.payments.update(key, patch));
      set({ providers: page.items, saving: false });
      return true;
    } catch (error) {
      // The API refuses an incomplete provider by design; its message names the
      // missing field, so it is shown rather than replaced.
      set({ saving: false, error: errorMessage(error, 'Could not save the provider.') });
      return false;
    }
  },
}));
